import { sendErrorToServices } from 'lib/logging';
import { useEffect, useRef } from 'react';

interface PlaybackStats {
  bufferCount: number;
  lastBufferTime: number;
  playbackStartTime: number;
  totalBufferTime: number;
  lastBufferLength: number;
  consecutiveStalls: number;
}

export function usePlaybackMonitoring(player: HTMLVideoElement | null) {
  const stats = useRef<PlaybackStats>({
    bufferCount: 0,
    lastBufferTime: 0,
    playbackStartTime: 0,
    totalBufferTime: 0,
    lastBufferLength: 0,
    consecutiveStalls: 0
  });

  useEffect(() => {
    if (!player) return;

    const hls = (player as any).hlsPlayer;
    if (!hls) return;

    const handleBufferStart = () => {
      const currentBufferLength =
        player.buffered.length > 0
          ? player.buffered.end(player.buffered.length - 1) - player.currentTime
          : 0;

      if (stats.current.lastBufferTime === 0) {
        stats.current.lastBufferTime = Date.now();
        stats.current.bufferCount++;
        stats.current.lastBufferLength = currentBufferLength;

        // Check if this is a consecutive stall
        if (Date.now() - stats.current.lastBufferTime < 5000) {
          stats.current.consecutiveStalls++;
        } else {
          stats.current.consecutiveStalls = 1;
        }

        sendErrorToServices('Playback Stall', {
          message: 'Buffer stall detected',
          timestamp: player.currentTime,
          bufferLength: currentBufferLength,
          consecutiveStalls: stats.current.consecutiveStalls,
          bandwidth: hls.bandwidthEstimate,
          currentLevel: hls.currentLevel
        });
      }
    };

    const handleBufferEnd = () => {
      if (stats.current.lastBufferTime > 0) {
        const stallDuration = Date.now() - stats.current.lastBufferTime;
        stats.current.totalBufferTime += stallDuration;
        stats.current.lastBufferTime = 0;

        // Log if buffering is excessive
        if (
          stats.current.bufferCount > 5 ||
          stats.current.totalBufferTime > 10000 ||
          stats.current.consecutiveStalls >= 3
        ) {
          sendErrorToServices('Playback Warning', {
            message: 'Excessive buffering detected',
            bufferCount: stats.current.bufferCount,
            totalBufferTime: stats.current.totalBufferTime,
            consecutiveStalls: stats.current.consecutiveStalls,
            bandwidth: hls.bandwidthEstimate,
            currentLevel: hls.currentLevel,
            stallDuration
          });
        }
      }
    };

    const handleLevelSwitch = data => {
      console.info('Quality Level Switch', {
        from: data.level,
        to: data.level,
        bitrate: hls.levels[data.level]?.bitrate,
        bufferLength:
          player.buffered.length > 0
            ? player.buffered.end(player.buffered.length - 1) -
              player.currentTime
            : 0
      });
    };

    // Monitor buffer health periodically
    const bufferMonitorInterval = setInterval(() => {
      if (player.readyState < 4) {
        // HAVE_ENOUGH_DATA
        const bufferLength =
          player.buffered.length > 0
            ? player.buffered.end(player.buffered.length - 1) -
              player.currentTime
            : 0;

        if (bufferLength < 0.5) {
          // Critical buffer level
          sendErrorToServices('Buffer Warning', {
            message: 'Critical buffer level',
            bufferLength,
            currentTime: player.currentTime,
            readyState: player.readyState
          });
        }
      }
    }, 2000);

    player.addEventListener('waiting', handleBufferStart);
    player.addEventListener('playing', handleBufferEnd);
    hls.on('hlsLevelSwitching', handleLevelSwitch);

    return () => {
      player.removeEventListener('waiting', handleBufferStart);
      player.removeEventListener('playing', handleBufferEnd);
      hls.off('hlsLevelSwitching', handleLevelSwitch);
      clearInterval(bufferMonitorInterval);
    };
  }, [player]);
}
